/**
 * Phase 36 — Gateways step 3, the Experiment card: start one (a lane,
 * share, days, metric), watch it run (day n of N, both sides so far, stop
 * early), and when it is over apply the winner or start another. A follower
 * that inherits its master's gateways reads the master's experiment here.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC, useState } from 'react';
import { FlaskConical } from 'lucide-react';
import { type DropGenExperimentRow, laneForDraft, laneLabel } from '@trafficpulse/shared';

import { StepCard } from '@/components/ui';
import { getApiErrorMessage } from '@/lib/api-error';
import { useAuth } from '@/providers/AuthProvider';

import type { ContainerEditor } from '../../state/useContainerEditor';
import { gwLetter } from '../cards/strategy-copy';
import { EXPERIMENT_HINT, experimentSummary } from './experiment-copy';
import { mixFor } from '@trafficpulse/shared';

import { draftTextMix } from '../../../shared/texts/text-mix';
import { ExperimentForm } from './ExperimentForm';
import { ExperimentProgress } from './ExperimentProgress';
import { ExperimentResult } from './ExperimentResult';
import { useExperimentMutations, useExperiments } from './use-experiments';

interface Props {
  editor: ContainerEditor;
  countryKey: string;
  /** Null until the container is saved. */
  containerId: string | null;
  routeName: (routeId: number) => string;
}

export const ExperimentStep: FC<Props> = ({ editor, countryKey, containerId, routeName }) => {
  const { draft } = editor;
  const canManage = useAuth().hasPermission('drop_generator.manage');
  const [another, setAnother] = useState(false);
  const { data } = useExperiments(countryKey, containerId !== null);
  const m = useExperimentMutations(countryKey);
  const inherits = draft.kind !== 'MASTER' && draft.masterId !== null && !(draft.ownSections.gatewayPool && draft.ownSections.gatewayStrategy);
  const mine = (data ?? []).filter((e) => e.containerId === containerId);
  const running = mine.find((e) => e.status === 'RUNNING');
  const latest = mine[0];
  const mastersRun = inherits ? (data ?? []).find((e) => e.containerId === draft.masterId && e.status === 'RUNNING') : undefined;
  // Phase 40 — the experiment names its lane: the pool's letter + "23Telecom · AUSSIEWIN" (an older one: its route's first lane).
  const label = (e: DropGenExperimentRow) => {
    const lane = laneForDraft(draft.gateway.pool, e.routeId, e.gatewayRowId);
    const at = lane ? draft.gateway.pool.indexOf(lane) : -1;
    const words = e.laneLabel ?? (lane ? laneLabel(lane, routeName(e.routeId)) : routeName(e.routeId));
    return at >= 0 ? `${gwLetter(at)} · ${words}` : words;
  };
  const busy = m.start.isPending || m.stop.isPending || m.apply.isPending || m.judge.isPending;
  const error = [m.start, m.stop, m.apply, m.judge].find((x) => x.isError)?.error ?? null;
  const body = (): JSX.Element => {
    if (containerId === null) return <Hint>Save the container first — an experiment runs on a saved container.</Hint>;
    if (inherits)
      return (
        <div className="flex flex-col gap-2">
          <Hint>{`${draft.name} follows its master config for gateways — experiments run on the master config and apply to every container that follows it.`}</Hint>
          {mastersRun ? <ExperimentProgress experiment={mastersRun} label={label(mastersRun)} canManage={false} busy={false} /> : null}
        </div>
      );
    if (running)
      return <ExperimentProgress experiment={running} label={label(running)} canManage={canManage} busy={busy} onStop={() => m.stop.mutate(running.id)} onRefresh={() => m.judge.mutate()} />;
    if (latest && !another)
      return (
        <ExperimentResult
          experiment={latest}
          label={label(latest)}
          containerName={draft.name}
          canManage={canManage}
          busy={busy}
          error={error ? getApiErrorMessage(error) : null}
          onApply={(action) => m.apply.mutate({ id: latest.id, action })}
          onStartAnother={() => setAnother(true)}
        />
      );
    return (
      <ExperimentForm
        pool={draft.gateway.pool}
        proven={mixFor(draftTextMix(draft), 'EXPERIMENT')}
        routeName={routeName}
        canManage={canManage}
        busy={busy}
        error={error ? getApiErrorMessage(error) : null}
        onStart={(bodyIn) => m.start.mutate({ containerId, body: bodyIn }, { onSuccess: () => setAnother(false) })}
      />
    );
  };
  return (
    <StepCard index={3} icon={FlaskConical} title="Experiment" summary={summaryOf(containerId, inherits, mastersRun, running, latest, label)}>
      <div className="flex flex-col gap-3">
        <p className="text-xs text-muted-foreground">{EXPERIMENT_HINT}</p>
        {body()}
      </div>
    </StepCard>
  );
};

const Hint: FC<{ children: string }> = ({ children }) => <p className="rounded-lg border border-dashed border-border px-4 py-3 text-sm text-muted-foreground">{children}</p>;

function summaryOf(
  containerId: string | null,
  inherits: boolean,
  mastersRun: DropGenExperimentRow | undefined,
  running: DropGenExperimentRow | undefined,
  latest: DropGenExperimentRow | undefined,
  label: (e: DropGenExperimentRow) => string,
): string {
  if (containerId === null) return 'save the container first';
  if (inherits) return mastersRun ? `the master config's — ${experimentSummary(mastersRun, undefined, label)}` : "runs on the master config — none running";
  return experimentSummary(running, latest, label);
}
